import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

/**
 * SignaturePad - simple canvas-based signature drawing.
 * Methods exposed via ref: clear(), isEmpty(), toDataURL()
 */
const SignaturePad = forwardRef(function SignaturePad(props, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasContentRef = useRef(false);

  const getContext = () => {
    const c = canvasRef.current;
    if (!c) return null;
    return c.getContext("2d");
  };

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
  };

  useEffect(() => {
    resize();
    const onR = () => resize();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = pos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = getContext();
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    hasContentRef.current = true;
  };
  const end = (e) => {
    if (drawingRef.current) e?.preventDefault?.();
    drawingRef.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      const ctx = getContext();
      const c = canvasRef.current;
      ctx.clearRect(0, 0, c.width, c.height);
      hasContentRef.current = false;
    },
    isEmpty: () => !hasContentRef.current,
    toDataURL: () => canvasRef.current?.toDataURL("image/png") || null,
  }));

  return (
    <canvas
      ref={canvasRef}
      data-testid="signature-canvas"
      className="lc-signature-canvas"
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
    />
  );
});

export default SignaturePad;
